import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useToast } from "@chakra-ui/react";
import { GET_TENANT_BY_CURRENT_CLIENT, UPDATE_TENANT } from "../queries";
import { TenantFormData } from "../types";

const emptyTenantFormData: TenantFormData = {
    id: "",
    name: "",
    domain: "",
    websiteUrl: "",
    apiKeys: {
        postmarkApiKey: "",
        cellcastApiKey: "",
        stripePublicKey: "",
        stripeSecretKey: "",
        stripeWebhookSecret: "",
    },
    branding: {
        siteName: "",
        tagline: "",
        description: "",
        logo: "",
        favicon: "",
        primaryColor: "",
        secondaryColor: "",
        accentColor: "",
    },
    emailConfig: {
        fromEmail: "",
        fromName: "",
        replyToEmail: "",
    },
};

/**
 * Custom hook for fetching + mutating the tenant tied to the current client.
 *
 * - `tenantFormData` mirrors the backend record into form-state (non-nullable
 *   defaults so controlled inputs stay controlled).
 * - `saveTenant()` issues the `UPDATE_TENANT` mutation iff the tenant exists.
 *   Returns void; toasts on success/error.
 *
 * @param userId - The current client ID; query is skipped when undefined
 */
export const useTenantData = (userId: string | undefined) => {
    const toast = useToast();
    const [tenantFormData, setTenantFormData] = useState<TenantFormData>(emptyTenantFormData);

    const { data: tenantData, loading: tenantLoading, refetch: refetchTenant } = useQuery(
        GET_TENANT_BY_CURRENT_CLIENT,
        {
            skip: !userId,
            onCompleted: (data) => {
                if (data?.tenantByCurrentClient) {
                    setTenantFormData({
                        id: data.tenantByCurrentClient.id,
                        name: data.tenantByCurrentClient.name || "",
                        domain: data.tenantByCurrentClient.domain || "",
                        websiteUrl: data.tenantByCurrentClient.websiteUrl || "",
                        apiKeys: {
                            postmarkApiKey: data.tenantByCurrentClient.apiKeys?.postmarkApiKey || "",
                            cellcastApiKey: data.tenantByCurrentClient.apiKeys?.cellcastApiKey || "",
                            stripePublicKey: data.tenantByCurrentClient.apiKeys?.stripePublicKey || "",
                            stripeSecretKey: data.tenantByCurrentClient.apiKeys?.stripeSecretKey || "",
                            stripeWebhookSecret: data.tenantByCurrentClient.apiKeys?.stripeWebhookSecret || "",
                        },
                        branding: {
                            siteName: data.tenantByCurrentClient.branding?.siteName || "",
                            tagline: data.tenantByCurrentClient.branding?.tagline || "",
                            description: data.tenantByCurrentClient.branding?.description || "",
                            logo: data.tenantByCurrentClient.branding?.logo || "",
                            favicon: data.tenantByCurrentClient.branding?.favicon || "",
                            primaryColor: data.tenantByCurrentClient.branding?.primaryColor || "",
                            secondaryColor: data.tenantByCurrentClient.branding?.secondaryColor || "",
                            accentColor: data.tenantByCurrentClient.branding?.accentColor || "",
                        },
                        emailConfig: {
                            fromEmail: data.tenantByCurrentClient.emailConfig?.fromEmail || "",
                            fromName: data.tenantByCurrentClient.emailConfig?.fromName || "",
                            replyToEmail: data.tenantByCurrentClient.emailConfig?.replyToEmail || "",
                        },
                    });
                }
            },
        }
    );

    const [updateTenant, { loading: updateTenantLoading }] = useMutation(UPDATE_TENANT, {
        onCompleted: () => {
            toast({
                title: "Website Settings Updated",
                description: "Your website settings have been updated successfully",
                status: "success",
                duration: 3000,
            });
            refetchTenant();
        },
        onError: (error) => {
            toast({
                title: "Update Failed",
                description: error.message,
                status: "error",
                duration: 5000,
            });
        },
    });

    const saveTenant = async () => {
        if (!tenantFormData.id) return;

        await updateTenant({
            variables: {
                id: tenantFormData.id,
                input: {
                    name: tenantFormData.name,
                    domain: tenantFormData.domain,
                    websiteUrl: tenantFormData.websiteUrl,
                    apiKeys: tenantFormData.apiKeys,
                    branding: tenantFormData.branding,
                    emailConfig: tenantFormData.emailConfig,
                },
            },
        });
    };

    return {
        tenantFormData,
        setTenantFormData,
        tenantData: tenantData?.tenantByCurrentClient,
        tenantLoading,
        saveTenant,
        updateTenantLoading,
    };
};
